import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import FormSelection from './FormSelection';
import { questionMapping } from '../lib/questionMapping';

const WorkshopApp = () => {
    const [searchParams] = useSearchParams();
    const formId = searchParams.get('form');
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState(0);
    const [answers, setAnswers] = useState({});
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showUnsubmitConfirm, setShowUnsubmitConfirm] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [user, setUser] = useState(null);
    const [lastSaved, setLastSaved] = useState(null);
    const formName = formId;

    const topics = [
        {
            id: '3d-growth-system',
            title: 'The 3D Growth System',
            subtitle: 'Unlock AI Opportunities',
            icon: '📈',
            key_concepts: [
                '📈 Revenue - Find, convert and expand customers',
                '⚡ Capacity - Scale support and success without hiring',
                '✨ Experience - Personalise every customer journey',
                'Discover the highest impact opportunities for your digital workforce'
            ],
            exercises: [
                {
                    title: 'Quick Check: Your Hidden Opportunities',
                    questions: [
                        'Which apply to your business? (tick all that apply)'
                    ]
                },
                {
                    title: 'Part 1: Revenue Dimension - Lead Response Analysis',
                    questions: [
                        'Enquiries per month:',
                        'Average deal value: £',
                        '% of enquiries outside office hours:',
                        'Current response time to these:',
                        '% contacted within 1 hour:',
                        '% contacted after 24 hours:',
                        'If you contacted 100% within 5 minutes, how many more would convert?',
                        'Weekend revenue currently lost: £'
                    ]
                },
                {
                    title: 'Part 1: Revenue Dimension - Database Mining Potential',
                    questions: [
                        'Total contacts in database:',
                        'Average deal value: £',
                        'Contacted in last 6 months:',
                        'Estimated % who might now be ready:',
                        'Hidden revenue in dormant database: £'
                    ]
                },
                {
                    title: 'Part 2: Capacity Dimension - Support Efficiency Audit',
                    questions: [
                        'Support tickets per month:',
                        '% that are repeat questions:',
                        'Time spent weekly on answering repetitive questions:',
                        'Time spent weekly on manual ticket routing:',
                        'Time spent weekly on writing similar responses:',
                        'Time spent weekly on after-hours coverage:',
                        'Total time that could be redirected:'
                    ]
                },
                {
                    title: 'Part 2: Capacity Dimension - Customer Success Scale',
                    questions: [
                        'Customers per Success Manager:',
                        'Average upsell value: £',
                        'Customer lifetime value: £',
                        'Current monthly churn rate:',
                        'Proactive check-ins per month:',
                        'If CSMs had 50% more time - Additional customers saved/month:',
                        'If CSMs had 50% more time - Additional upsell conversations:',
                        'Revenue protected/expanded with more capacity: £'
                    ]
                },
                {
                    title: 'Part 3: Experience Dimension - Personalisation Assessment',
                    questions: [
                        '% getting generic experience:',
                        'Average products/services per customer:',
                        'Cross-sell conversion rate:',
                        'Could personalisation increase spend per customer?',
                        'Example for your business: What journey would delight your best customer segment?',
                        'Additional revenue per customer with personalisation: £'
                    ]
                },
                {
                    title: 'Part 4: Priority & Impact - Your Opportunity Matrix',
                    questions: [
                        'Rate Weekend response (Impact 1-10):',
                        'Rate Weekend response (Speed 1-10):',
                        'Rate Database reactivation (Impact 1-10):',
                        'Rate Database reactivation (Speed 1-10):',
                        'Rate Support automation (Impact 1-10):',
                        'Rate Support automation (Speed 1-10):',
                        'Rate Success scaling (Impact 1-10):',
                        'Rate Success scaling (Speed 1-10):',
                        'Rate Personalisation (Impact 1-10):',
                        'Rate Personalisation (Speed 1-10):',
                        'Your #1 Opportunity:'
                    ]
                },
                {
                    title: 'Part 4: Priority & Impact - Deep Dive: Your Biggest Win',
                    questions: [
                        'What\'s broken today:',
                        'Hours wasted weekly:',
                        'Revenue lost monthly: £',
                        'What success looks like:',
                        'Month 1 impact: £',
                        'Month 6 impact: £',
                        'Year 1 impact: £'
                    ]
                },
                {
                    title: 'Part 5: Making It Happen - Reality Check',
                    questions: [
                        'Are competitors doing this?',
                        'Why haven\'t you done it yet?',
                        'What\'s the cost of waiting another month? £'
                    ]
                },
                {
                    title: 'Part 5: Making It Happen - Your Readiness',
                    questions: [
                        'When do you want this fixed?',
                        'What would help you most?',
                        'Preferred next step:'
                    ]
                },
                {
                    title: 'Part 5: Making It Happen - Your Commitment',
                    questions: [
                        'The ONE opportunity I will pursue:',
                        'Revenue I will capture: £',
                        'By when:',
                        'Tomorrow I will:'
                    ]
                }
            ]
        },
        {
            id: 'ai-helpdesk',
            title: 'The 24/7 Department',
            subtitle: 'AI-Powered Helpdesks',
            icon: '⚡️',
            key_concepts: [
                'AI agents as capacity multipliers for departments',
                'Continuous 24/7 customer / team support capabilities',
                'Enhanced analytics & insights for inquiry classification',
                'Human-AI collaboration models and handoff protocols'
            ],
            exercises: [
                {
                    title: 'Departmental Constraint Analysis',
                    questions: [
                        'List 3 departments that could benefit from AI support to scale:',
                        'Estimate weekly time spent by senior team supporting items that AI could handle:',
                        'Calculate potential ROI: (Senior person hourly rate × hours worked on support items)'
                    ]
                },
                {
                    title: 'Conversation Flow Design',
                    questions: [
                        'Your most common customer or employee inquiry:',
                        'Design a basic AI conversation flow & resources needed:',
                        'Define information gathering questions:',
                        'Outline resolution paths and escalation criteria:'
                    ]
                },
                {
                    title: 'Human-AI Integration',
                    questions: [
                        'Identify 3 trigger points for human handoff:',
                        'Create criteria for handoff determination:',
                        'Design knowledge transfer protocol:'
                    ]
                }
            ]
        },
        {
            id: 'data-streams',
            title: 'Breaking From The Pack',
            subtitle: 'AI-Powered Data Streams',
            icon: '📊',
            key_concepts: [
                'Converting real-time data streams into competitive advantage',
                'Pattern recognition in operational and customer data',
                'Predictive analytics for business forecasting',
                'Anomaly detection for proactive intervention'
            ],
            exercises: [
                {
                    title: 'Data Stream Inventory',
                    questions: [
                        'List key data streams in your organisation:',
                        'Identify underutilised data streams:',
                        'Select the most valuable data streams for AI enhancement:'
                    ]
                },
                {
                    title: 'High-Value Prediction',
                    questions: [
                        'Select one operational area for prediction:',
                        'Define the specific prediction needed:',
                        'List required data sources and current gaps:'
                    ]
                },
                {
                    title: 'Insight Visualisation',
                    questions: [
                        'Choose the reports you want for AI analysed insights:',
                        'List 3-5 key metrics for decision-making:',
                        'Define update frequency and access permissions:'
                    ]
                }
            ]
        },
        {
            id: 'ai-first',
            title: 'AI-First Business',
            subtitle: 'Unlocking New Possibilities',
            icon: '🚀',
            key_concepts: [
                'Creating entirely new business models possible only through AI',
                'Identifying market whitespace that AI uniquely enables',
                'Reimagining products and services with embedded intelligence',
                'Designing experiences that would be impossible without AI'
            ],
            exercises: [
                {
                    title: 'New Business Possibility Canvas',
                    questions: [
                        'Identify 3 new business possibilities enabled by AI:',
                        'Who are the new customers or underserved segments?',
                        'What unique value propositions would these create?',
                        'What traditional business constraints would this overcome?'
                    ]
                },
                {
                    title: 'Experience Reimagination',
                    questions: [
                        'Select one current customer journey:',
                        'Reimagine it with AI as the foundation:',
                        'Map new touchpoints and personalization opportunities:',
                        'Describe how this creates 10x improvement:'
                    ]
                },
                {
                    title: 'AI-Native Venture Design',
                    questions: [
                        'Design a new venture built around AI capabilities:',
                        'Core AI technology advantage:',
                        'Data strategy and compounding advantage mechanism:',
                        'Potential disruption to existing business models:'
                    ]
                }
            ]
        },
        {
            id: 'integrated-ai-os',
            title: 'Connected Agents = Exponential Value',
            subtitle: 'Integrated AI Operating System',
            icon: '🧠',
            key_concepts: [
                'Moving beyond one-off AI tools to integrated systems',
                'Human workforce and digital workforce collaboration',
                'Connected agents creating exponential value',
                'AI orchestration across business processes'
            ],
            exercises: [
                {
                    title: 'AI OS Blueprint',
                    questions: [
                        'Map your current departments with highest impact potential for AI transformation:',
                        'Identify connection points between different AI agents:',
                        'Design information flow between human and digital workforce:'
                    ]
                },
                {
                    title: 'Agent Network Design',
                    questions: [
                        'List 3-5 potential AI agents in your organisation:',
                        'Define how these agents would communicate:',
                        'Outline the human touchpoints in this network:'
                    ]
                },
                {
                    title: 'Value Amplification Strategy',
                    questions: [
                        'Identify how connected agents create more value than isolated ones:',
                        'Calculate potential efficiency gains from an integrated approach:',
                        'Design metrics to measure the network effect of your AI OS:'
                    ]
                }
            ]
        }
    ];

    useEffect(() => {
        const loadSavedAnswers = async () => {
            const userData = JSON.parse(localStorage.getItem('workshopUser'));
            if (userData) {
                setUser(userData);
                try {
                    const { data, error } = await supabase
                        .from('workshop_answers')
                        .select('values')
                        .eq('email', userData.email)
                        .eq('form_name', formName)
                        .order('updated_at', { ascending: false })
                        .limit(1)
                        .single();

                    if (error) throw error;
                    if (data) {
                        const processedAnswers = {};
                        Object.entries(data.values).forEach(([key, value]) => {
                            // Handle different data types properly
                            if (typeof value === 'string') {
                                // Try to parse JSON strings (for arrays/objects)
                                try {
                                    const parsed = JSON.parse(value);
                                    processedAnswers[key] = parsed;
                                } catch {
                                    // If not JSON, use as string
                                    processedAnswers[key] = value;
                                }
                            } else if (Array.isArray(value)) {
                                // Direct array handling
                                processedAnswers[key] = value;
                            } else if (typeof value === 'object' && value !== null) {
                                // Object handling
                                processedAnswers[key] = value;
                            } else {
                                // Fallback to string or empty
                                processedAnswers[key] = value || '';
                            }
                        });
                        setAnswers(processedAnswers);
                        setLastSaved(new Date(data.updated_at));
                    }
                } catch (error) {
                    console.error('Error loading saved answers:', error);
                }
            }
        };
        if (formId) {
            loadSavedAnswers();
        }
    }, [formId, formName]);

    useEffect(() => {
        if (!user?.email || !formId) return;

        const saveAnswers = async () => {
            try {
                // Process answers to handle complex data types
                const processedAnswers = {};
                Object.entries(answers).forEach(([key, value]) => {
                    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                        // Serialize arrays and objects as JSON strings
                        processedAnswers[key] = JSON.stringify(value);
                    } else {
                        // Keep strings and primitives as-is
                        processedAnswers[key] = value;
                    }
                });

                const { error } = await supabase
                    .from('workshop_answers')
                    .upsert({
                        email: user.email,
                        form_name: formName,
                        values: processedAnswers,
                        updated_at: new Date().toISOString(),
                    }, {
                        onConflict: 'email,form_name'
                    });

                if (error) throw error;
                setLastSaved(new Date());
            } catch (error) {
                console.error('Error saving answers:', error);
            }
        };

        const saveTimeout = setTimeout(saveAnswers, 5000);
        return () => clearTimeout(saveTimeout);
    }, [answers, user?.email, formName, formId]);

    const handleLogout = () => {
        localStorage.removeItem('workshopUser');
        navigate('/');
    };

    const handleAnswerChange = (answerKey, value) => {
        // Ensure the answerKey has the form name prefix
        const fullKey = answerKey.startsWith(formName) ? answerKey : `${formName}-${answerKey}`;
        setAnswers(prev => ({
            ...prev,
            [fullKey]: value
        }));
    };

    // If no form is specified, show the form selection page
    if (!formId) {
        return <FormSelection />;
    }

    const activeTopic = topics[activeTab];

    return (
        <div className="min-h-screen bg-[#F8F8F8]">
            {/* Header */}
            <div className="w-full bg-white shadow-md rounded-t-lg overflow-hidden sticky top-0 z-50">
                <div className="flex flex-col bg-[#FAF9F6]">
                    <div className="flex justify-center py-3 border-b border-gray-200">
                        <img src="/iai-logo.png" alt="Implement AI" className="h-8 w-auto" />
                    </div>
                    <div className="p-3">
                        <h1 className="text-xl md:text-2xl font-semibold text-black text-center mb-3">AI Workshop: Building Your Digital Workforce</h1>
                        <div className="flex flex-col md:flex-row items-center justify-between gap-3">
                            <div className="flex items-center gap-2 text-black">
                                <span className="text-sm md:text-base">Welcome, {user?.name}</span>
                                {lastSaved && !isNaN(lastSaved.getTime()) && (
                                    <span className="text-xs md:text-sm">
                                        Last saved: {lastSaved.toLocaleTimeString()}
                                    </span>
                                )}
                            </div>
                            <div className="flex gap-2 w-full md:w-auto justify-center md:justify-end">
                                <button
                                    onClick={handleLogout}
                                    className="px-4 py-1.5 text-sm bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#7B3FE3] transition-colors duration-200"
                                >
                                    Logout
                                </button>
                                <button
                                    onClick={() => {
                                        const saveAnswers = async () => {
                                            try {
                                                // Process answers to handle complex data types
                                                const processedAnswers = {};
                                                Object.entries(answers).forEach(([key, value]) => {
                                                    if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
                                                        // Serialize arrays and objects as JSON strings
                                                        processedAnswers[key] = JSON.stringify(value);
                                                    } else {
                                                        // Keep strings and primitives as-is
                                                        processedAnswers[key] = value;
                                                    }
                                                });

                                                const { error } = await supabase
                                                    .from('workshop_answers')
                                                    .upsert({
                                                        email: user.email,
                                                        form_name: formName,
                                                        values: processedAnswers,
                                                        updated_at: new Date().toISOString(),
                                                    }, {
                                                        onConflict: 'email,form_name'
                                                    });

                                                if (error) throw error;
                                                setLastSaved(new Date());
                                            } catch (error) {
                                                console.error('Error saving answers:', error);
                                            }
                                        };
                                        saveAnswers();
                                    }}
                                    className="px-4 py-1.5 text-sm bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#7B3FE3] transition-colors duration-200"
                                    disabled={isSubmitted}
                                >
                                    Save
                                </button>
                                {!isSubmitted ? (
                                    <button
                                        onClick={() => setShowSubmitConfirm(true)}
                                        className="px-4 py-1.5 text-sm bg-[#8C52FF] text-white font-semibold rounded-lg hover:bg-[#7B3FE3] transition-colors duration-200"
                                    >
                                        Submit
                                    </button>
                                ) : (
                                    <button
                                        onClick={() => setShowUnsubmitConfirm(true)}
                                        className="px-4 py-1.5 text-sm bg-[#FF5252] text-white font-semibold rounded-lg hover:bg-[#FF3232] transition-colors duration-200"
                                    >
                                        Unsubmit
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex overflow-x-auto">
                    {topics.map((topic, index) => (
                        <button
                            key={topic.id}
                            className={`px-3 md:px-6 py-2 md:py-3 text-sm md:text-lg font-medium transition-colors duration-200 whitespace-nowrap ${activeTab === index ? 'text-white bg-[#8C52FF]' : 'text-gray-700 hover:bg-[#E8E3FF]'
                                }`}
                            onClick={() => setActiveTab(index)}
                        >
                            <span className="mr-2 md:mr-3">{topic.icon}</span>
                            {topic.title}
                        </button>
                    ))}
                </div>
            </div>

            {/* Main Content */}
            <div className="container mx-auto px-2 md:px-4 py-4 md:py-6">
                <div className="bg-white rounded-lg shadow-md">
                    <div className="p-3 md:p-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-black">{activeTopic.title}</h2>
                        <h3 className="text-lg md:text-xl text-black mb-4">{activeTopic.subtitle}</h3>

                        <div className="p-3 md:p-4 rounded-lg mb-4 md:mb-6 bg-[#E8E3FF]">
                            <h4 className="text-base md:text-lg font-semibold mb-2 text-black">Key Concepts</h4>
                            <ul className="list-disc pl-4 md:pl-5 space-y-1 md:space-y-2">
                                {activeTopic.key_concepts.map((concept, i) => (
                                    <li key={i} className="text-sm md:text-base text-gray-800">{concept}</li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="px-3 md:px-6 pb-4 md:pb-6 space-y-4 md:space-y-6">
                        {activeTopic.exercises.map((exercise, exerciseIndex) => (
                            <div key={exerciseIndex} className="p-3 md:p-6 rounded-lg border-2 border-[#B19EFF] bg-white">
                                <h4 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-black">
                                    Exercise {exerciseIndex + 1}: {exercise.title}
                                </h4>
                                <div className="space-y-3 md:space-y-4">
                                    {exercise.questions.map((question, questionIndex) => {
                                        const answerKey = `${activeTopic.id}-q-${exerciseIndex}-${questionIndex}`;
                                        const questionData = questionMapping[formId]?.[activeTopic.id]?.exercises[exerciseIndex]?.questions[questionIndex];
                                        
                                        return (
                                            <div key={answerKey} className="space-y-1 md:space-y-2">
                                                <label className="block text-sm md:text-base text-gray-700 font-medium">{question}</label>
                                                
                                                {questionData?.questionType === 'checkbox' ? (
                                                    <div className="space-y-2">
                                                        {questionData.options.map((option, optionIndex) => {
                                                            const optionKey = `${answerKey}-option-${optionIndex}`;
                                                            const currentAnswer = answers[`${formName}-${answerKey}`];
                                                            // Handle both array and string (JSON) formats
                                                            let answerArray = [];
                                                            if (Array.isArray(currentAnswer)) {
                                                                answerArray = currentAnswer;
                                                            } else if (typeof currentAnswer === 'string') {
                                                                try {
                                                                    answerArray = JSON.parse(currentAnswer || '[]');
                                                                } catch (e) {
                                                                    // If JSON parsing fails, treat as empty array
                                                                    answerArray = [];
                                                                }
                                                            }
                                                            const isChecked = answerArray.includes(option);
                                                            
                                                            return (
                                                                <label key={optionKey} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isChecked}
                                                                        onChange={(e) => {
                                                                            const currentAnswer = answers[`${formName}-${answerKey}`];
                                                                            let currentAnswers = [];
                                                                            if (Array.isArray(currentAnswer)) {
                                                                                currentAnswers = currentAnswer;
                                                                            } else if (typeof currentAnswer === 'string') {
                                                                                try {
                                                                                    currentAnswers = JSON.parse(currentAnswer || '[]');
                                                                                } catch (e) {
                                                                                    currentAnswers = [];
                                                                                }
                                                                            }
                                                                            let newAnswers;
                                                                            if (e.target.checked) {
                                                                                newAnswers = [...currentAnswers, option];
                                                                            } else {
                                                                                newAnswers = currentAnswers.filter(item => item !== option);
                                                                            }
                                                                            handleAnswerChange(answerKey, newAnswers);
                                                                        }}
                                                                        disabled={isSubmitted}
                                                                        className="rounded border-gray-300 text-[#8C52FF] focus:ring-[#8C52FF]"
                                                                    />
                                                                    <span className="text-sm md:text-base text-gray-700">{option}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : questionData?.questionType === 'radio' ? (
                                                    <div className="space-y-2">
                                                        {questionData.options.map((option, optionIndex) => {
                                                            const optionKey = `${answerKey}-option-${optionIndex}`;
                                                            const currentAnswer = answers[`${formName}-${answerKey}`];
                                                            // Handle both string and JSON string formats
                                                            let selectedValue = currentAnswer;
                                                            if (typeof currentAnswer === 'string') {
                                                                if (currentAnswer.startsWith('[') || currentAnswer.startsWith('{')) {
                                                                    try {
                                                                        selectedValue = JSON.parse(currentAnswer || '""');
                                                                    } catch (e) {
                                                                        selectedValue = currentAnswer;
                                                                    }
                                                                }
                                                            }
                                                            const isSelected = selectedValue === option;
                                                            
                                                            return (
                                                                <label key={optionKey} className="flex items-center space-x-2">
                                                                    <input
                                                                        type="radio"
                                                                        name={answerKey}
                                                                        value={option}
                                                                        checked={isSelected}
                                                                        onChange={(e) => handleAnswerChange(answerKey, e.target.value)}
                                                                        disabled={isSubmitted}
                                                                        className="border-gray-300 text-[#8C52FF] focus:ring-[#8C52FF]"
                                                                    />
                                                                    <span className="text-sm md:text-base text-gray-700">{option}</span>
                                                                </label>
                                                            );
                                                        })}
                                                    </div>
                                                ) : (
                                                    <textarea
                                                        className="w-full p-2 md:p-3 border rounded-md focus:ring-2 focus:ring-[#8C52FF] focus:border-[#8C52FF] min-h-[120px] md:min-h-[140px] resize-y text-sm md:text-base disabled:bg-gray-100 disabled:text-gray-700"
                                                        value={answers[`${formName}-${answerKey}`] || ''}
                                                        onChange={(e) => handleAnswerChange(answerKey, e.target.value)}
                                                        placeholder="Your answer here..."
                                                        disabled={isSubmitted}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Submit Confirmation Modal */}
            {showSubmitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-4 md:p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Confirm Submission</h3>
                        <p className="text-sm md:text-base mb-4 md:mb-6">Are you sure you want to submit your answers? This action can be undone later.</p>
                        <div className="flex justify-end space-x-3 md:space-x-4">
                            <button
                                onClick={() => setShowSubmitConfirm(false)}
                                className="px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                                No, Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setIsSubmitted(true);
                                    setShowSubmitConfirm(false);
                                }}
                                className="px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base bg-[#8C52FF] text-white rounded-lg hover:bg-[#6B3FCC] transition-colors duration-200"
                            >
                                Yes, Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Unsubmit Confirmation Modal */}
            {showUnsubmitConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white p-4 md:p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">Confirm Unsubmit</h3>
                        <p className="text-sm md:text-base mb-4 md:mb-6">Are you sure you want to unsubmit your answers? This will allow you to edit them again.</p>
                        <div className="flex justify-end space-x-3 md:space-x-4">
                            <button
                                onClick={() => setShowUnsubmitConfirm(false)}
                                className="px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                            >
                                No, Cancel
                            </button>
                            <button
                                onClick={() => {
                                    setIsSubmitted(false);
                                    setShowUnsubmitConfirm(false);
                                }}
                                className="px-3 md:px-4 py-1.5 md:py-2 text-sm md:text-base bg-[#FF5252] text-white rounded-lg hover:bg-[#FF3232] transition-colors duration-200"
                            >
                                Yes, Unsubmit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Footer */}
            <footer className="mt-6 md:mt-8 py-3 md:py-4 text-center text-sm md:text-base text-gray-600">
                ©2025 Implement AI. All rights reserved.
            </footer>
        </div>
    );
};

export default WorkshopApp;